// src/frontend_react/src/pages/UserDashboard/MyAccountPage.js
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { QRCodeCanvas } from 'qrcode.react';
import { notify } from '../../services/notificationService';

// API services
import { initiateMfaSetup, verifyAndEnableMfa } from '../../services/api';

// UI Components
import Card from '../../components/ui/Card/Card';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Typography from '../../components/ui/Typography/Typography';
import Button from '../../components/ui/Button/Button';
import Icon from '../../components/ui/Icon/Icon';
import Alert from '../../components/ui/Alert/Alert';
import Input from '../../components/ui/Input/Input';
import Modal from '../../components/ui/Modal/Modal';
import Spinner from '../../components/ui/Spinner/Spinner';
import Box from '../../components/ui/Box/Box';

// Icons
import { FiUser, FiShield, FiKey } from 'react-icons/fi';

const MyAccountPage = () => {
  const { user, checkAuthStatus } = useAuth(); // Use checkAuthStatus to refresh user state
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [mfaSetupDetails, setMfaSetupDetails] = useState(null);
  const [mfaVerificationCode, setMfaVerificationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  if (!user) {
    return <Spinner />;
  }

  const handleOpenMfaSetup = async () => {
    setIsMfaModalOpen(true);
    setIsSubmitting(true);
    setModalError('');
    setMfaSetupDetails(null);
    try {
      const response = await initiateMfaSetup(); // This API is self-service (no userId needed)
      if (response.success) {
        setMfaSetupDetails(response);
      } else {
        setModalError(response.message || 'Failed to initiate MFA setup.');
      }
    } catch (err) {
      setModalError(err.message || 'An error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (!mfaVerificationCode || mfaVerificationCode.length !== 6) {
      setModalError('Please enter a valid 6-digit code.');
      return;
    }
    setIsSubmitting(true);
    setModalError('');
    try {
      const response = await verifyAndEnableMfa(mfaVerificationCode);
      if (response.success) {
        notify.success('MFA enabled successfully!');
        await checkAuthStatus(); // Refresh user context to get is_totp_enabled = true
        setIsMfaModalOpen(false);
      } else {
        setModalError(response.message || 'Verification failed. Please try again.');
      }
    } catch (err) {
      setModalError(err.message || 'An error occurred during verification.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing="lg">
      <Typography variant="h2" component="h1">My Account</Typography>
      
      <Card title="Profile Information" titleIcon={<Icon as={FiUser} />}>
        <Stack p="lg" spacing="md">
          <HStack justifyContent="space-between"><Typography color="secondary">Username:</Typography> <Typography fontWeight="medium">{user.username}</Typography></HStack>
          <HStack justifyContent="space-between"><Typography color="secondary">Email:</Typography> <Typography fontWeight="medium">{user.email}</Typography></HStack>
          <HStack justifyContent="space-between"><Typography color="secondary">Name:</Typography> <Typography fontWeight="medium">{`${user.first_name} ${user.last_name}`}</Typography></HStack>
          <HStack justifyContent="space-between"><Typography color="secondary">Organization:</Typography> <Typography fontWeight="medium">{user.organization}</Typography></HStack>
        </Stack>
      </Card>

      <Card title="Security Settings" titleIcon={<Icon as={FiShield} />}>
        <Stack p="lg" spacing="md">
          <Typography fontWeight="medium">Multi-Factor Authentication (MFA)</Typography>
          {user.is_totp_enabled ? (
            <Alert variant="success">MFA is currently enabled for your account.</Alert>
          ) : (
            <>
              <Alert variant="warning">MFA is not enabled. For enhanced security, it is highly recommended to set it up.</Alert>
              <Button onClick={handleOpenMfaSetup} leftIcon={<Icon as={FiKey} />} style={{alignSelf: 'flex-start'}}>
                Setup MFA
              </Button>
            </>
          )}
        </Stack>
      </Card>

      {/* MFA Setup Modal */}
      <Modal
        isOpen={isMfaModalOpen}
        onClose={() => !isSubmitting && setIsMfaModalOpen(false)}
        title="Setup Multi-Factor Authentication"
        size="md"
      >
        <Stack spacing="md">
          {modalError && <Alert variant="error" onDismiss={() => setModalError('')}>{modalError}</Alert>}
          {isSubmitting && !mfaSetupDetails ? (
            <Box textAlign="center" p="xl"><Spinner /></Box>
          ) : mfaSetupDetails && (
            <>
              <Typography fontWeight="medium">1. Scan QR with Authenticator App</Typography>
              <Box textAlign="center" p="md" border="1px solid var(--cipher-color-border-default)" borderRadius="md" bg="white" alignSelf="center">
                <QRCodeCanvas value={mfaSetupDetails.provisioning_uri} size={200} />
              </Box>
              <Typography variant="body2" color="secondary" textAlign="center">
                Or manually enter setup key: <Typography component="code">{mfaSetupDetails.secret}</Typography>
              </Typography>
              <hr style={{width: '100%', borderTop: '1px solid var(--cipher-color-border-subtle)'}}/>
              <Typography fontWeight="medium">2. Verify and Enable</Typography>
              <Input
                label="6-Digit Verification Code"
                value={mfaVerificationCode}
                onChange={(e) => setMfaVerificationCode(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                disabled={isSubmitting}
                autoFocus
              />
              <HStack justifyContent="flex-end">
                <Button variant="outline" onClick={() => setIsMfaModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
                <Button onClick={handleVerifyAndEnable} isLoading={isSubmitting} disabled={isSubmitting || mfaVerificationCode.length !== 6}>Verify & Enable</Button>
              </HStack>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
};

export default MyAccountPage;